import React from "react";
import { useLocation } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import {
  SITE_URL,
  SEO_BY_PATH,
  getSeoForPath,
} from "../../lib/seoConfig";
import { getStructuredData } from "../../lib/structuredData";

/**
 * Route-aware document head.
 *
 * Mounted once inside <BrowserRouter> (see App.js) instead of per page, so
 * every route in seoConfig.js gets its title/description without touching
 * sixteen page components. Unlisted routes fall back to DEFAULT_SEO, which
 * mirrors public/index.html.
 *
 * Page components deliberately do not render their own <Helmet>: two canonicals
 * on one page is worse than none, and titles drifting between here and a page
 * component is how a route ends up advertising the wrong thing.
 */
export default function Seo() {
  const { pathname } = useLocation();

  const normalized =
    pathname.length > 1 ? pathname.replace(/\/+$/, "").toLowerCase() : "/";
  const { title, description, noindex } = getSeoForPath(pathname);

  // Only canonicalise routes we actually own. The "*" route renders NotFound
  // for any unmatched path, so a blanket self-canonical would mint canonicals
  // for URLs that shouldn't exist.
  const canonical =
    normalized === "/" || SEO_BY_PATH[normalized]
      ? `${SITE_URL}${normalized === "/" ? "/" : normalized}`
      : null;

  // Structured data is what answer engines quote for hard facts (what the
  // product is, what it costs, what it runs on). Only emitted for routes we
  // canonicalise: an unmatched URL shouldn't claim to be the product page.
  const structuredData = canonical && !noindex ? getStructuredData(pathname) : null;

  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />

      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content="Zero Typing AI" />
      {canonical && <meta property="og:url" content={canonical} />}

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />

      {canonical && <link rel="canonical" href={canonical} />}
      {noindex && <meta name="robots" content="noindex, follow" />}

      {/* max-snippet/max-image-preview let Google and AI Overviews quote the
          page at full length instead of truncating it to a stub. */}
      {!noindex && (
        <meta
          name="robots"
          content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1"
        />
      )}

      {structuredData && (
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      )}
    </Helmet>
  );
}
